import React, { useMemo, useState } from "react";
import Tabs from "react-bootstrap/Tabs";
import Tab from "react-bootstrap/Tab";
import Title from "../../../../../components/Title";
import { RlCloudSandboxNetworkItems } from "./RlCloudSandboxNetworkItems";
import RlCloudSandboxGrid from "../RlCloudSandboxGrid";
import { DYNAMIC_ANALYSIS_EMPTY_MESSAGES } from "../consts";
import {
  NetworkData,
  NetworkDataItem,
  NetworkDataKey
} from "../../../@types/rldynamicanalysis";
import { NetworkRepEntry } from "../../../../network/@types/Url";
import { sortNetworkReputationData } from "../../report_summary/hooks";
import useBulkNetworkReputation from "../../../../network/hooks/useBulkNetworkReputation";

type RlCloudSandboxNetworkProps = {
  networkData: NetworkData;
  reportTitle: string;
};

type NetworkDataItemWithReputation = NetworkDataItem & Partial<NetworkRepEntry>;

const getUrlOrIp = (item: NetworkDataItem) => {
  if ("url" in item) {
    return item.url;
  } else if ("destination_ip" in item) {
    return item.destination_ip;
  } else {
    return item.value;
  }
};

const mergeNetworkAndRepuationData = (
  networkData: NetworkDataItem[],
  reputationDataEntries?: NetworkRepEntry[]
): NetworkDataItemWithReputation[] => {
  if (!reputationDataEntries)
    return networkData as NetworkDataItemWithReputation[];

  const reputationDataMap = reputationDataEntries.reduce(
    (acc, entry) => ({
      ...acc,
      [entry.requested_network_location]: entry
    }),
    {} as Record<string, NetworkRepEntry>
  );

  return networkData
    .map(item => ({
      ...item,
      ...reputationDataMap[getUrlOrIp(item)]
    }))
    .sort(sortNetworkReputationData);
};

const RlCloudSandboxNetwork = ({
  networkData,
  reportTitle
}: RlCloudSandboxNetworkProps) => {
  const [activeTab, setActiveTab] = useState<NetworkDataKey>("http");
  const activeData = useMemo(
    () => networkData?.[activeTab as NetworkDataKey] ?? [],
    [activeTab, networkData]
  );

  const urlsOrIps = useMemo(() => activeData.map(getUrlOrIp), [activeData]);

  const { data, isLoading } = useBulkNetworkReputation(urlsOrIps);

  const enrichedData = useMemo(
    () => mergeNetworkAndRepuationData(activeData, data),
    [activeData, data]
  );

  const handleTabsSelect = (tabsKey: NetworkDataKey) => {
    setActiveTab(tabsKey);
  };

  return (
    <>
      <Title Level="h2" text={`Network Analysis (${reportTitle})`} />

      {networkData ? (
        <Tabs
          id="rl-cloud-sanddbox-network-tabs"
          variant="tabs"
          activeKey={activeTab}
          onSelect={handleTabsSelect}
          className="rlbs"
          transition={false}
          mountOnEnter
        >
          {Object.entries(RlCloudSandboxNetworkItems).map(([key, value]) => {
            const { title, schema } = value;
            return (
              <Tab
                key={key}
                eventKey={key}
                title={`${title} (${
                  networkData?.[key as NetworkDataKey]?.length || 0
                })`}
              >
                <div data-testid={`rlcs-network-tab_${title}`}>
                  <RlCloudSandboxGrid
                    data={enrichedData || []}
                    schema={schema}
                    showLoading={isLoading}
                  />
                </div>
              </Tab>
            );
          })}
        </Tabs>
      ) : (
        <b>{DYNAMIC_ANALYSIS_EMPTY_MESSAGES.network_analysis}</b>
      )}
    </>
  );
};

export default RlCloudSandboxNetwork;
